import { PermissionEnum } from '@sr-sdks/api-sdk-axios';
import { useQuery } from '@tanstack/react-query';
import { lazy } from 'react';
import { Outlet, RouteObject } from 'react-router-dom';
import { prefetchWorkspaces } from '../api/prefetch';
import { queryOptionsGetFeatures } from '../api/shared/settingsApi';
import ErrorPage from '../components/ErrorPage';
import IfFeatureEnabledMSR3 from '../components/IfFeatureEnabledMSR3';
import { MigrationSwitch } from '../components/MigrationSwitch';
import { NavigateWithParams } from '../components/NavigateWithParams';
import { SingleQueryResults } from '../components/QueryResults/SingleQueryResults';
import { FEATURE_FLAGS } from '../constant/featureFlags';
import DashboardLayout from '../layout/DashboardLayout';
import ProfileLayout from '../layout/ProfileLayout';
import { MyProfile as MyProfileV3 } from '../pages/v3/MyProfile/MyProfile';
import { ProtectLazyLoadRoute } from './LazyLoadElement';
import ProtectRoute from './ProtectRoute';

const Notifications = lazy(() => import('../pages/Profile/Notifications'));

/**
 * Not `MigrationSwitch`: its sticky `?v=2` preference would 404 this route
 * while the nav links, which ignore it, stayed visible. `SingleQueryResults`
 * covers the loading and error states the two-branch form has nowhere to put.
 */
const NotificationsRoute = () => {
  const featuresQuery = useQuery(queryOptionsGetFeatures());

  return (
    <SingleQueryResults query={featuresQuery}>
      {() => (
        <>
          <IfFeatureEnabledMSR3 name={FEATURE_FLAGS.MSR3_NOTIFICATIONS_ROUTE}>
            <ProfileLayout>{ProtectLazyLoadRoute(Notifications)}</ProfileLayout>
          </IfFeatureEnabledMSR3>

          <IfFeatureEnabledMSR3
            name={FEATURE_FLAGS.MSR3_NOTIFICATIONS_ROUTE}
            isEnabled="false"
          >
            {/* `DashboardLayout`, not `ProfileLayout`: not-found renders
                identically everywhere in the app, and `ProfileLayout`'s
                content card shrink-wraps and left-aligns the error block. */}
            <DashboardLayout>
              <ErrorPage notFound />
            </DashboardLayout>
          </IfFeatureEnabledMSR3>
        </>
      )}
    </SingleQueryResults>
  );
};

export const ProfileRoutes: RouteObject[] = [
  {
    // Single `ProtectRoute` covers the whole `/profile` subtree — children
    // do not need to re-wrap. `ProtectLazyLoadRoute` already adds its own
    // protect-on-lazy-resolve so it composes safely beneath this one.
    element: (
      <ProtectRoute>
        <Outlet />
      </ProtectRoute>
    ),
    children: [
      {
        index: true,
        element: <NavigateWithParams replace to="my-profile" />,
      },
      {
        path: 'my-profile',
        element: (
          <MigrationSwitch
            featureFlags={[
              FEATURE_FLAGS.MSR3_ENABLED,
              FEATURE_FLAGS.MSR3_PROFILE_ROUTE,
            ]}
            newComponent={<MyProfileV3 />}
            oldComponent={
              <ProfileLayout>
                {ProtectLazyLoadRoute(
                  lazy(() => import('../pages/Profile/MyProfile')),
                )}
              </ProfileLayout>
            }
          />
        ),
        loader: () => {
          prefetchWorkspaces();

          return {
            title: 'My Profile',
          };
        },
      },
      {
        path: 'notifications',
        element: <NotificationsRoute />,
        loader: () => {
          prefetchWorkspaces();

          return {
            permissionRequired: [PermissionEnum.ViewNotificationFeed],
            title: 'Notifications',
          };
        },
      },
    ],
  },
];
